import { Box, Button, Flex, HStack, IconButton } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import GradientPopover from 'components/GradientPopover';
import Icon from 'components/Icon';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';

import AssetsPanel from './AssetsDrawer/AssetsPanel';
import CharacterCounter from './CodeEditor/CharacterCounter';
import ConsolePanel, { ConsoleButton } from './ConsolePanel';
import { useDevSandbox } from './DevSandboxProvider';
import SaveIndicator from './SaveIndicator';
import ShortcutsButton from './ShortcutsButton';
import { TourPill, TourSpotlight, useTour, TTourId } from './Tour';
import { CODE_FREE_LIMIT, CODE_PAID_LIMIT } from './constants';
import { TDevSandboxFooterQuickButton } from './types';
import useSandboxTheme from './useSandboxTheme';

interface IQuickButtonProps extends TDevSandboxFooterQuickButton {
  browserUrl: string;
}

const QuickButton = ({
  title,
  icon,
  content,
  autoOpen,
  browserUrl,
  tooltip,
}: IQuickButtonProps) => {
  const [isOpened, setIsOpened] = useState(false);

  return (
    <GradientPopover
      width="500px"
      tooltip={tooltip}
      triggerComponent={
        <IconButton
          aria-label="tooltip"
          variant="ghost"
          backgroundColor={isOpened ? 'purple.400' : 'inherit'}
          transition="background-color 0.2s"
          _hover={{ backgroundColor: 'purple.400' }}
        >
          {icon}
        </IconButton>
      }
      onPopoverStateChange={setIsOpened}
      header={title}
      id={browserUrl}
      defaultIsOpen={Boolean(autoOpen)}
    >
      {content}
    </GradientPopover>
  );
};

// Inline SVG icon for Assets button — matches ShortcutsButton/ConsoleButton pattern
const AssetsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const AssetsButton = ({ onClick, isActive, colors }: { onClick: () => void; isActive: boolean; colors: { textSecondary: string; textMuted: string; hoverBg: string; controlBg: string; activeBg: string } }) => {
  return (
    <Button
      aria-label="Toggle assets"
      leftIcon={<Box as="span" display="flex"><AssetsIcon /></Box>}
      size="xs"
      variant="ghost"
      color={isActive ? colors.textSecondary : colors.textMuted}
      bg={isActive ? colors.controlBg : 'transparent'}
      h="28px"
      fontSize="11px"
      fontWeight="500"
      _hover={{ color: colors.textSecondary, bg: colors.controlBg }}
      _active={{ bg: colors.activeBg }}
      onClick={onClick}
    >
      Assets
    </Button>
  );
};

const DevSandboxFooter = () => {
  const { isMobile } = useMediaQueries();
  const { isFree } = useCurrentUser();
  const { colors } = useSandboxTheme();
  const [isConsoleOpen, setIsConsoleOpen] = useState(false);
  const [isAssetsOpen, setIsAssetsOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);

  const {
    browserUrl,
    mode,
    entityType,
    footer,
    codeTabs: { html, css, js, tabIndex },
    merlin: { isOpen: isMerlinOpen },
  } = useDevSandbox();

  const assetsConfig = footer.assetsConfig;
  const hideTools = footer.hideTools;
  const showForkHint = footer.showForkHint !== false;

  // Derive tour ID from entity context
  const tourId: TTourId | null =
    mode === 'script-practice'
      ? 'practice'
      : entityType === 'project'
        ? 'project'
        : entityType === 'challenge'
          ? 'challenge'
          : entityType === 'code-frame' || entityType === 'prototype'
            ? 'codeframe'
            : null;

  const tour = useTour(tourId);

  // Mutual exclusivity: opening one panel closes the others
  const openShortcuts = useCallback(() => {
    setIsShortcutsOpen(true);
    setIsAssetsOpen(false);
    setIsConsoleOpen(false);
  }, []);

  const toggleAssets = useCallback(() => {
    setIsAssetsOpen((prev) => {
      if (!prev) {
        setIsConsoleOpen(false);
        setIsShortcutsOpen(false);
      }
      return !prev;
    });
  }, []);

  const toggleConsole = useCallback(() => {
    setIsConsoleOpen((prev) => {
      if (!prev) {
        setIsAssetsOpen(false);
        setIsShortcutsOpen(false);
      }
      return !prev;
    });
  }, []);

  const closeAllPanels = useCallback(() => {
    setIsAssetsOpen(false);
    setIsConsoleOpen(false);
    setIsShortcutsOpen(false);
  }, []);

  // Stop tour only when Merlin transitions from closed → open
  const prevMerlinOpen = useRef(isMerlinOpen);
  useEffect(() => {
    if (isMerlinOpen && !prevMerlinOpen.current && tour.isActive) {
      tour.stop();
    }
    prevMerlinOpen.current = isMerlinOpen;
  }, [isMerlinOpen, tour.isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTourStart = useCallback(() => {
    closeAllPanels();
    tour.start();
  }, [closeAllPanels, tour.start]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-switch pane tabs when tour step has navigateToTab
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!tour.isActive || tour.currentStep < 0) return;
    const step = tour.steps[tour.currentStep];
    if (!step?.navigateToTab) return;

    // Only navigate if the tab isn't already active
    if (!location.pathname.includes(step.navigateToTab)) {
      navigate(step.navigateToTab, { replace: true });
    }
  }, [tour.isActive, tour.currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard shortcut: Cmd/Ctrl+Shift+A to toggle Assets panel
  // Disabled when viewing solutions (hideTools = true)
  useEffect(() => {
    if (!assetsConfig || hideTools) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        e.stopPropagation();
        toggleAssets();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [assetsConfig, hideTools, toggleAssets]);

  if (mode === 'full-page' || mode === 'embed') {
    return null;
  }

  // Calculate current chars based on active tab
  // Tab order is always: [HTML (0), CSS-variant (1), JS-variant (2)]
  const index = tabIndex || 0;
  const isHTMLTab = index === 0;
  const isCSSTab = index === 1;
  const currentChars = isHTMLTab ? (html?.length ?? 0) : isCSSTab ? (css?.length ?? 0) : (js?.length ?? 0);
  const maxChars = isFree ? CODE_FREE_LIMIT : CODE_PAID_LIMIT;

  // Solution view: minimal footer or hidden entirely
  if (hideTools) {
    if (!showForkHint) return null;

    return (
      <Box position="relative">
        <Box borderTop="1px solid" borderColor={colors.shellBorder} position="relative" zIndex={999} />
        <Flex
          height="60px"
          backgroundColor={colors.shellBg}
          px={isMobile ? 2 : 4}
          alignItems="center"
        >
          <HStack spacing={1.5}>
            <Icon type="ForkCode" size={14} color={colors.textMuted} />
            <Text fontSize="micro" opacity="tertiary">
              Fork this solution to edit in your own editor
            </Text>
          </HStack>
        </Flex>
      </Box>
    );
  }

  const isAnyPanelOpen = isAssetsOpen || isConsoleOpen || isShortcutsOpen;

  return (
    <Box position="relative">
      {/* Backdrop overlay — closes all panels when clicking outside (including iframe/preview) */}
      {isAnyPanelOpen && (
        <Box
          position="fixed"
          inset={0}
          zIndex={998}
          onClick={closeAllPanels}
        />
      )}

      {/* Console panel - pops up from footer */}
      <ConsolePanel isOpen={isConsoleOpen} onToggle={toggleConsole} />

      {/* Assets floating panel - pops up from footer, bottom-left */}
      {assetsConfig && (
        <AssetsPanel
          isOpen={isAssetsOpen}
          onClose={() => setIsAssetsOpen(false)}
          config={assetsConfig}
        />
      )}

      <Box borderTop="1px solid" borderColor={colors.shellBorder} position="relative" zIndex={999} />
      <Flex
        height="60px"
        backgroundColor={colors.shellBg}
        px={isMobile ? 2 : 4}
        alignItems="center"
        position="relative"
      >
        {/* Left section: Assets → Console → Shortcuts */}
        <HStack spacing={1}>
          {!isMobile && (
            <>
              {assetsConfig && (
                <Box data-tour="footer-assets">
                  <AssetsButton
                    onClick={toggleAssets}
                    isActive={isAssetsOpen}
                    colors={colors}
                  />
                </Box>
              )}
              <HStack spacing={1}>
                <Box data-tour="console-button" display="inline-flex">
                  <ConsoleButton onClick={toggleConsole} isActive={isConsoleOpen} />
                </Box>
                <Box data-tour="shortcuts-button" display="inline-flex">
                  <ShortcutsButton
                    mode={mode}
                    isOpen={isShortcutsOpen}
                    onOpen={openShortcuts}
                    onClose={() => setIsShortcutsOpen(false)}
                    isActive={isShortcutsOpen}
                  />
                </Box>
              </HStack>
            </>
          )}
        </HStack>

        {/* Custom footer left content + char counter */}
        <HStack ml={!isMobile ? 3 : 0} spacing={3}>
          {footer.leftSide || null}
          <CharacterCounter currentChars={currentChars} maxChars={maxChars} isFree={isFree} />
        </HStack>

        {/* Center: Save indicator (very subtle) */}
        {!isMobile && (
          <Box position="absolute" left="50%" transform="translateX(-50%)">
            <SaveIndicator />
          </Box>
        )}

        {/* Right section: custom rightSide quick buttons + tour pill */}
        <HStack spacing={4} ml="auto">
          {footer.rightSide
            ? footer.rightSide.map((button) => (
                <QuickButton key={button.id} {...button} browserUrl={browserUrl || ''} />
              ))
            : null}
          {tourId && (
            <Box data-tour="tour-pill" display="inline-flex">
              <TourPill
                isProminent={tour.isProminent}
                isActive={tour.isActive}
                onClick={handleTourStart}
              />
            </Box>
          )}
        </HStack>
      </Flex>

      {/* Tour spotlight overlay */}
      {tour.isActive && tour.currentStep >= 0 && (
        <TourSpotlight
          step={tour.steps[tour.currentStep]}
          stepIndex={tour.currentStep}
          totalSteps={tour.totalSteps}
          onNext={tour.next}
          onPrev={tour.prev}
          onSkip={tour.skip}
          isMerlinOpen={isMerlinOpen}
          isAutoStarted={tour.isAutoStarted}
        />
      )}
    </Box>
  );
};

export default DevSandboxFooter;
